// The web radio's audio: one <audio> element on the Icecast stream, with the
// two things a live stream in a browser needs and a media file does not.
//
//  1. A status line instead of a start screen. play() is attempted at once
//     and "Connecting to radio…" shows until audio actually flows. Browsers
//     refuse audible autoplay before the page has been interacted with; in
//     that one case the line says so and the first click/touch/key anywhere
//     starts the stream. Contexts that allow autoplay (a kiosk, the TV's web
//     view) go straight from "Connecting" to playing.
//
//  2. Reconnection. A live stream ends when the encoder hiccups, the tunnel
//     drops or the browser's buffer starves; a media element then just sits
//     on 'ended'/'error' forever. Any of those, or a playhead that has not
//     advanced for STALL_MS while supposedly playing, tears the element down
//     and re-attaches the stream with a cache-busting query, backing off up
//     to BACKOFF_MAX_MS.
//
// Up/Down on the remote adjust THIS listener's volume (the element's), not
// Soloist's: Soloist's volume is the master level of the stream everyone
// hears and is pinned at 100% by its service unit.
const VOLUME_STEP = 0.1;
const VOLUME_STORAGE_KEY = 'piradio.volume';
const STALL_MS = 8000;
const BACKOFF_MIN_MS = 1000;
const BACKOFF_MAX_MS = 15000;

// Un geste fait DANS le tiroir (ouvrir la poignée, taper une recherche, faire
// défiler les radios) n'est pas une demande d'écoute : il ne doit pas
// déclencher la lecture restée bloquée par le navigateur. Seul un choix de
// station le fait, et celui-là appelle play() lui-même, dans le geste.
function gestureAsksForSound(e) {
  const t = e && e.target;
  if (!t || !t.closest) return true;
  if (t.closest('.station')) return true;      // choisir une radio : oui
  return !t.closest('#sheet');                 // le reste du tiroir : non
}

export class RadioPlayer {
  // options: { audioEl, streamUrl, statusEl, onVolume(level 0..1),
  //            onStatus(text), onPauseChange(paused) }
  constructor(options) {
    this._audio = options.audioEl;
    this._streamUrl = options.streamUrl;
    this._statusEl = options.statusEl || null;
    this._blocked = false;
    this._onVolume = options.onVolume || null;
    this._onStatus = options.onStatus || null;
    // Called with true/false when the listener pauses/resumes THIS page's
    // audio (the artwork tap - see main-radio.js). Distinct from the
    // reconnect path: a paused player is not a broken one.
    this._onPauseChange = options.onPauseChange || null;
    this._paused = false;
    // Optional one-off hint shown in the status line the first time audio
    // flows: { text, ms, touchOnly }. touchOnly restricts it to touch
    // screens (the swipe hint means nothing to a mouse or a remote).
    this._hint = options.playingHint || null;
    this._hintShown = false;
    this._hintTimer = null;
    this._backoffMs = BACKOFF_MIN_MS;
    this._reconnectTimer = null;
    this._lastProgressAt = performance.now();
    this._lastTime = -1;
    this._wanted = false;

    this._audio.preload = 'none';
    this._audio.volume = this._loadVolume();

    const a = this._audio;
    // L'INSTANT DE CONNEXION au flux, pour le retard du lecteur (voir
    // retardLecteurSecondes) : chaque reconnexion repart de zéro.
    this._connexionA = null;
    this._premierSon = null;   // {t, ct} : l'instant et la position du premier son de cette connexion
    a.addEventListener('loadstart', () => { if (this._connexionA == null) this._connexionA = performance.now(); });
    a.addEventListener('playing', () => {
      if (this._premierSon == null && this._connexionA != null) this._premierSon = { t: performance.now(), ct: a.currentTime };
      this._backoffMs = BACKOFF_MIN_MS;
      this._blocked = false;
      this._status('');
      this._maybeShowHint();
    });
    a.addEventListener('timeupdate', () => {
      if (a.currentTime !== this._lastTime) {
        this._lastTime = a.currentTime;
        this._lastProgressAt = performance.now();
      }
    });
    a.addEventListener('ended', () => this._scheduleReconnect('Stream ended'));
    a.addEventListener('error', () => this._scheduleReconnect('Stream error'));
    a.addEventListener('stalled', () => { /* covered by the watchdog below */ });

    // Watchdog: a live stream that stops advancing is as dead as one that
    // errored, the element just does not say so.
    setInterval(() => {
      if (!this._wanted || a.paused) return;
      if (performance.now() - this._lastProgressAt > STALL_MS) {
        this._scheduleReconnect('Stream stalled');
      }
    }, 2000);

    // Autoplay refused: the very next gesture anywhere starts the stream.
    // Gated on _blocked so that, once playing, keys and clicks keep their
    // own meanings (Enter is the lyrics toggle, the cover is pause).
    this._unblockedAt = 0;
    const go = (e) => {
      if (!gestureAsksForSound(e)) return;
      if (!this._blocked) return;
      this._blocked = false;
      this._unblockedAt = performance.now();
      this.start();
    };
    window.addEventListener('click', go, true);
    window.addEventListener('touchend', go, true);
    window.addEventListener('keydown', go, true);
  }

  start() {
    this._wanted = true;
    this._status('Connecting to radio…');
    this._attach();
    const p = this._audio.play();
    if (p && typeof p.catch === 'function') {
      p.catch((err) => {
        // NotAllowedError = autoplay policy: wait for a gesture. Anything
        // else is a stream problem: the reconnect path owns it.
        if (err && err.name === 'NotAllowedError') {
          this._blocked = true;
          this._status('Tap anywhere to start the sound');
        } else {
          this._scheduleReconnect('Stream unavailable');
        }
      });
    }
  }

  // The listener's own pause. The radio keeps running for everyone else,
  // so resuming does not continue where it left off: it re-attaches the
  // stream and rejoins at the live edge, like a real radio would.
  get paused() { return this._paused; }

  pause() {
    if (this._paused) return;
    this._paused = true;
    this._wanted = false;
    clearTimeout(this._reconnectTimer);
    this._reconnectTimer = null;
    this._audio.pause();
    this._audio.removeAttribute('src');
    this._audio.load();
    this._status('');
    if (this._onPauseChange) this._onPauseChange(true);
  }

  resume() {
    if (!this._paused) {
      // Never started (a page opened straight on an external station):
      // resuming means starting.
      if (!this._wanted) this.start();
      return;
    }
    this._paused = false;
    if (this._onPauseChange) this._onPauseChange(false);
    this.start();
  }

  toggle() {
    // The tap/key that just lifted the autoplay block must not also pause:
    // it was "start", not "pause". See the capture-phase gesture listener.
    if (performance.now() - this._unblockedAt < 800) return;
    if (this._paused) this.resume();
    else this.pause();
  }

  stop() {
    this._wanted = false;
    clearTimeout(this._reconnectTimer);
    this._audio.pause();
    this._audio.removeAttribute('src');
    this._audio.load();
  }

  // direction: +1 / -1. Returns the new level (0..1) for the on-screen readout.
  stepVolume(direction) {
    const next = Math.min(1, Math.max(0, Math.round((this._audio.volume + direction * VOLUME_STEP) * 10) / 10));
    this._audio.volume = next;
    this._saveVolume(next);
    if (this._onVolume) this._onVolume(next);
    return next;
  }

  // LE TEMPS PASSÉ SANS JOUER depuis la connexion au flux : démarrage,
  // mises en mémoire tampon, pauses. C'est la part de la latence propre à CE
  // lecteur — elle varie d'un navigateur à l'autre et d'une connexion à
  // l'autre (mesuré le 28/09/2026 : 3,5 à 3,7 s dans Chrome sans affichage).
  // La latence entendue vaut une part fixe (Pi, Icecast, tunnel : 2,7 s)
  // plus celle-ci — voir soloist-visual-profile.js. null tant qu'on ne sait
  // pas (pas connecté, pas encore de son).
  retardLecteurSecondes() {
    // Indépendant de l'origine de currentTime, que les navigateurs ne
    // traitent pas tous pareil sur un flux en direct (Safari) : le démarrage
    // (connexion → premier son) plus ce que le lecteur a perdu depuis
    // (temps écoulé depuis le premier son − ce qu'il a joué depuis).
    const a = this._audio;
    const p = this._premierSon;
    if (!a || this._connexionA == null || !p || a.paused) return null;
    const demarrage = (p.t - this._connexionA) / 1000;
    const perdu = (performance.now() - p.t) / 1000 - (a.currentTime - p.ct);
    const r = demarrage + perdu;
    return Number.isFinite(r) && r >= 0 && r < 60 ? r : null;
  }


  // Le son coule-t-il vraiment ? (le signal d'écoute ne compte que ce qui
  // joue, pas une page ouverte sur une radio en pause)
  get playing() { return this._wanted && this._audio && !this._audio.paused; }

  get volume() { return this._audio.volume; }

  _attach() {
    // A fresh query string per attach defeats any cache in between and makes
    // Icecast hand out a new burst so playback resumes at the live edge.
    this._connexionA = performance.now();
    this._premierSon = null;
    this._audio.src = this._streamUrl + (this._streamUrl.indexOf('?') === -1 ? '?' : '&') + 't=' + Date.now();
    this._lastTime = -1;
    this._lastProgressAt = performance.now();
  }

  _scheduleReconnect(reason) {
    if (!this._wanted || this._reconnectTimer) return;
    this._status(reason + ' — reconnecting…');
    this._reconnectTimer = setTimeout(() => {
      this._reconnectTimer = null;
      if (!this._wanted) return;
      this._attach();
      const p = this._audio.play();
      if (p && typeof p.catch === 'function') p.catch(() => this._scheduleReconnect('Reconnect failed'));
    }, this._backoffMs);
    this._backoffMs = Math.min(BACKOFF_MAX_MS, this._backoffMs * 2);
  }

  _maybeShowHint() {
    const h = this._hint;
    if (!h || this._hintShown) return;
    if (h.touchOnly) {
      const touch = (typeof window.matchMedia === 'function' && window.matchMedia('(pointer: coarse)').matches)
        || 'ontouchstart' in window;
      if (!touch) return;
    }
    this._hintShown = true;
    this._status(h.text);
    clearTimeout(this._hintTimer);
    this._hintTimer = setTimeout(() => {
      // Only clear our own text: a reconnect notice that replaced it stays.
      if (this._statusEl && this._statusEl.textContent === h.text) this._status('');
    }, h.ms || 6000);
  }

  _status(text) {
    if (this._statusEl) {
      this._statusEl.textContent = text;
      this._statusEl.classList.toggle('visible', !!text);
    }
    if (this._onStatus) this._onStatus(text);
  }

  _loadVolume() {
    try {
      const raw = window.localStorage.getItem(VOLUME_STORAGE_KEY);
      const n = raw == null ? NaN : Number(raw);
      if (Number.isFinite(n) && n >= 0 && n <= 1) return n;
    } catch (e) { /* storage unavailable: default stands */ }
    return 0.8;
  }

  _saveVolume(v) {
    try { window.localStorage.setItem(VOLUME_STORAGE_KEY, String(v)); } catch (e) { /* ignore */ }
  }
}
